import { useEffect, useState } from 'react';
import { useEmployee } from '../context/EmployeeContext';
import LifeRail from '../../icib-admin/components/LifeRail';

/* EE-01 — See all benefits an employee is covered for.
   "The system shall allow employees to see all benefits they are covered
   for." — helps employees understand the value of their employment
   package (BrandonUserStoryAPI.md EE-01, GET /me/employees).

   One card per employer group the signed-in person belongs to (§2.4 — one
   Employee row per group), each listing its enrolments (§2.5) with the
   same life-rail status indicator the ICIB console uses. EE-01's response
   is deliberately lean — employee_id / employer_client / enrolments only,
   just `product_id` per enrolment, no product name or insurer link — so
   the list alone only has the benefit's id. To show the benefit name, each
   scheme's EE-02 detail (GET /me/employees/{id}) is also loaded here via
   EmployeeContext#loadSchemeDetail — the same cache the "Benefit details"
   tab reads, so opening that tab afterwards needs no extra fetch. Until a
   scheme's detail arrives its rows show "Loading…"; if that fetch fails
   they fall back to the bare `product_id`. */
function CoverView() {
  const { db, status, refreshing, schemeDetails, loadSchemeDetail } = useEmployee();
  // employee_id -> true for schemes whose EE-02 fetch failed, so their rows
  // stop showing "Loading…" and fall back to the bare product_id.
  const [detailFailed, setDetailFailed] = useState({});

  // One EE-02 fetch per scheme not already cached. loadSchemeDetail isn't
  // stable across renders (EmployeeContext doesn't memoize it) — key off
  // the scheme list alone, same pattern as SchemeView.
  useEffect(() => {
    db.employees.forEach((emp) => {
      if (!emp.id || schemeDetails[emp.id]) return;
      const markFailed = () => setDetailFailed((m) => ({ ...m, [emp.id]: true }));
      loadSchemeDetail(emp.id)
        .then((res) => { if (res?.error) markFailed(); })
        .catch(markFailed);
    });
  }, [db.employees]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'loading') {
    return <div className="card"><div className="empty"><b>Loading…</b></div></div>;
  }
  if (status === 'error' || !db.me) {
    return (
      <div className="card">
        <div className="empty">
          <b>Couldn't load your cover</b>
          Check your connection and try again.
        </div>
      </div>
    );
  }

  const schemes = db.employees;

  return (
    <>
      <div className="eyebrow">Enrolment Overview</div>
      <div className="page-h"><h2>Enrolment Overview</h2></div>
      <p className="page-sub">
        Every benefit you're covered for, across every employer scheme you belong to.{refreshing ? ' Refreshing…' : ''}
      </p>

      {db.errors?.employees && (
        <div className="note bad" style={{ marginBottom: 14 }}>
          <b>Couldn't load your cover</b>{db.errors.employees}
        </div>
      )}

      <div className="sec-h">
        <h3>Your cover</h3>
        <span className="spacer"></span>
        <span className="ep">GET /me/employees</span>
      </div>

      {schemes.length ? (
        schemes.map((emp) => {
          // enrolment_id -> product, from this scheme's EE-02 detail (if loaded).
          const detail = schemeDetails[emp.id];
          const productByEnrolment = Object.fromEntries(
            (detail?.enrolments || []).map((en) => [en.id, en.product]),
          );
          const namesLoading = !detail && !detailFailed[emp.id];
          return (
          <div className="card card-b" key={emp.id} style={{ marginBottom: 14 }}>
            <div className="sec-h" style={{ marginTop: 0 }}>
              <h3>{emp.employer_client?.name || 'Employer scheme'}</h3>
            </div>
            {emp.enrolments?.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Benefit</th><th>Status</th></tr></thead>
                  <tbody>
                    {emp.enrolments.map((en) => {
                      const product = productByEnrolment[en.enrolment_id];
                      return (
                      <tr key={en.enrolment_id}>
                        {product?.name
                          ? <td>{product.name}</td>
                          : namesLoading
                            ? <td className="dim">Loading…</td>
                            : <td className="mono">{en.product_id}</td>}
                        <td><LifeRail status={en.status} withLabel /></td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty"><b>No benefits yet</b>Nothing enrolled under this scheme yet.</div>
            )}
          </div>
          );
        })
      ) : (
        <div className="card"><div className="empty"><b>No cover yet</b>You're not currently enrolled in any employer scheme.</div></div>
      )}
    </>
  );
}

export default CoverView;
